/* eslint-disable no-param-reassign */
/* eslint-disable indent */
import React from 'react';
import { Chip, makeStyles, TextField } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import clsx from 'clsx';
import Loading from 'components/Loading';
import { cyrb53Hash, regexExp } from 'utils/helpers';
import i18n from 'i18n';
import styles from './styles';

const useStyles = makeStyles(styles);

export interface Props {
  value: string[];
  loading?: boolean;
  active?: boolean;
  onChange: (value: string[]) => void;
  onError: (error: boolean) => void;
}

const FormAllowedIps = ({
  value,
  loading,
  active,
  onChange,
  onError,
}: Props) => {
  const classes = useStyles();

  const [hasError] = React.useState(false);
  const [defaultValue, setDefaultValue] = React.useState<string[]>([]);

  const handleChange = (newValues: string[]) => {
    if (newValues?.some(value => !value)) return;

    const value = newValues?.filter(
      (value, index) => newValues?.indexOf(value) === index
    );

    onChange(value);
    setDefaultValue(value);
  };

  const handleRemove = (value: string) => {
    const newValue = defaultValue.filter(item => item !== value);
    handleChange(newValue);
  };

  const renderTags = (values: string[]) => {
    if (values.length === 0) return <></>;

    const tags = values.map((label: string) => ({
      label,
      isValidIP: regexExp.ipNumber?.test(label),
    }));

    return tags.map((option: { label: string; isValidIP: boolean }) => (
      <Chip
        key={cyrb53Hash(option?.label)}
        classes={
          !option?.isValidIP
            ? {
                outlined: classes.chipError,
                icon: classes.chipIcon,
                deleteIcon: classes.chipDeleteIcon,
              }
            : {}
        }
        icon={!option?.isValidIP ? <WarningIcon fontSize="small" /> : <></>}
        variant="outlined"
        label={option?.label}
        onDelete={() => handleRemove(option?.label)}
      />
    ));
  };

  const handleInputChange = event => {
    if (!Number(event?.target?.value) && !event?.target?.value?.includes('.')) {
      event.target.value = '';
    }

    if (event?.key === 'Enter') {
      handleChange([...defaultValue, event?.target?.value]);
      event.target.value = '';
    }
  };

  function handleOnBlurChange(event) {
    handleChange([...defaultValue, event?.target?.value]);
    event.target.value = '';
  }

  React.useEffect(() => {
    onError(hasError);
  }, [onError, hasError]);

  React.useEffect(() => {
    if (value) {
      setDefaultValue(value.filter(Boolean));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return loading ? (
    <Loading />
  ) : (
    <TextField
      fullWidth
      id="token-valid-ips"
      data-testid={'autosky-formallowedips'}
      placeholder={defaultValue?.length <= 0 ? '000.000.0.0' : ''}
      error={active && defaultValue?.length <= 0}
      label={i18n.t('IPS_VALID_KEY')}
      variant="outlined"
      onBlur={handleOnBlurChange}
      className={clsx(classes.autocomplete, {
        [classes.autocompleteError]: hasError,
      })}
      InputProps={{
        startAdornment: renderTags(defaultValue),
        onKeyPress: handleInputChange,
        onChange: handleInputChange,
      }}
      inputProps={{
        maxLength: 50,
      }}
    />
  );
};

export default FormAllowedIps;
